import { Directive, ElementRef, Input, OnDestroy, Renderer2, HostListener } from '@angular/core';
import { PaletteDetailModel } from '../models/palettes/palette_detail_model';
import { BeadImageService } from '../services/bead-image.service';

type BeadPreviewLike = PaletteDetailModel | {
  beadName?: string;
  beadColor?: string;
  name?: string;
  color?: string;
  description?: string;
  paletteId?: number | null;
  originalPaletteId?: number | null;
};

@Directive({
  selector: '[appBeadPreview]'
})
export class BeadPreviewDirective implements OnDestroy {
  @Input('appBeadPreview') bead: BeadPreviewLike | null = null;
  @Input() previewPaletteId: number | null = null;

  private tooltipElement: HTMLElement | null = null;
  private showTimeout: any = null;
  private isTooltipVisible: boolean = false;

  constructor(
    private el: ElementRef,
    private renderer: Renderer2,
    private beadImageService: BeadImageService
  ) {}

  @HostListener('mouseenter', ['$event'])
  onMouseEnter(event: MouseEvent): void {
    // Delay showing the tooltip by 300ms
    this.showTimeout = setTimeout(() => {
      this.showTooltip(event);
    }, 300);
  }

  @HostListener('mousemove', ['$event'])
  onMouseMove(event: MouseEvent): void {
    if (this.isTooltipVisible && this.tooltipElement) {
      this.positionTooltip(event);
    }
  }

  @HostListener('mouseleave')
  onMouseLeave(): void {
    this.hideTooltip();
  }

  ngOnDestroy(): void {
    this.hideTooltip();
  }

  private async showTooltip(event: MouseEvent): Promise<void> {
    if (!this.bead || this.tooltipElement) {
      return;
    }

    const beadName = this.getBeadName(this.bead);
    const beadColor = this.getBeadColor(this.bead);
    const beadDescription = this.getBeadDescription(this.bead);

    // Try current palette first, then fall back to original palette for custom palettes
    let paletteId = this.previewPaletteId || this.bead.paletteId || null;
    let imagePath = paletteId && beadName
      ? this.beadImageService.getBeadImagePath(paletteId, beadName)
      : null;
    let hasImage = Boolean(
      paletteId &&
      beadName &&
      imagePath &&
      await this.beadImageService.checkImageExists(paletteId, beadName)
    );

    // If no image found and we have an original palette ID, try that
    if (!hasImage && this.bead.originalPaletteId && this.bead.originalPaletteId !== paletteId) {
      paletteId = this.bead.originalPaletteId;
      imagePath = beadName ? this.beadImageService.getBeadImagePath(paletteId, beadName) : null;
      hasImage = Boolean(
        beadName &&
        imagePath &&
        await this.beadImageService.checkImageExists(paletteId, beadName)
      );
    }

    // Create tooltip element
    this.tooltipElement = this.renderer.createElement('div');
    this.renderer.addClass(this.tooltipElement, 'bead-preview-tooltip');

    // Create content container
    const contentContainer = this.renderer.createElement('div');
    this.renderer.addClass(contentContainer, 'bead-preview-content');

    if (hasImage && imagePath) {
      // Create image element
      const imageElement = this.renderer.createElement('img');
      this.renderer.addClass(imageElement, 'bead-preview-image');
      this.renderer.setAttribute(imageElement, 'src', imagePath);
      if (beadName) {
        this.renderer.setAttribute(imageElement, 'alt', beadName);
      }
      this.renderer.appendChild(contentContainer, imageElement);
    } else {
      // Create color swatch fallback
      const swatchElement = this.renderer.createElement('div');
      this.renderer.addClass(swatchElement, 'bead-preview-swatch');
      if (beadColor) {
        this.renderer.setStyle(swatchElement, 'background-color', '#' + beadColor);
      }
      this.renderer.appendChild(contentContainer, swatchElement);
    }

    // Create info section
    const infoElement = this.renderer.createElement('div');
    this.renderer.addClass(infoElement, 'bead-preview-info');

    const nameElement = this.renderer.createElement('div');
    this.renderer.addClass(nameElement, 'bead-preview-name');
    if (beadName) {
      const nameText = this.renderer.createText(beadName);
      this.renderer.appendChild(nameElement, nameText);
    }
    this.renderer.appendChild(infoElement, nameElement);

    if (beadDescription) {
      const descElement = this.renderer.createElement('div');
      this.renderer.addClass(descElement, 'bead-preview-description');
      const descText = this.renderer.createText(beadDescription);
      this.renderer.appendChild(descElement, descText);
      this.renderer.appendChild(infoElement, descElement);
    }

    this.renderer.appendChild(contentContainer, infoElement);
    this.renderer.appendChild(this.tooltipElement, contentContainer);

    // Append to body
    this.renderer.appendChild(document.body, this.tooltipElement);

    // Position tooltip
    this.positionTooltip(event);

    // Trigger animation
    setTimeout(() => {
      if (this.tooltipElement) {
        this.renderer.addClass(this.tooltipElement, 'visible');
      }
    }, 10);

    this.isTooltipVisible = true;
  }

  private positionTooltip(event: MouseEvent): void {
    if (!this.tooltipElement) {
      return;
    }

    const tooltipWidth = 320;
    const tooltipHeight = 380;
    const offset = 15;
    const viewportWidth = window.innerWidth;
    const viewportHeight = window.innerHeight;

    let left = event.clientX + offset;
    let top = event.clientY + offset;

    // Adjust horizontal position if tooltip would go off screen
    if (left + tooltipWidth > viewportWidth) {
      left = event.clientX - tooltipWidth - offset;
    }

    // Adjust vertical position if tooltip would go off screen
    if (top + tooltipHeight > viewportHeight) {
      top = event.clientY - tooltipHeight - offset;
    }

    // Ensure tooltip doesn't go off the left or top edge
    left = Math.max(10, left);
    top = Math.max(10, top);

    this.renderer.setStyle(this.tooltipElement, 'left', left + 'px');
    this.renderer.setStyle(this.tooltipElement, 'top', top + 'px');
  }

  private hideTooltip(): void {
    // Clear any pending show timeout
    if (this.showTimeout) {
      clearTimeout(this.showTimeout);
      this.showTimeout = null;
    }

    // Remove tooltip element
    if (this.tooltipElement) {
      this.renderer.removeChild(document.body, this.tooltipElement);
      this.tooltipElement = null;
    }

    this.isTooltipVisible = false;
  }

  private getBeadName(bead: BeadPreviewLike): string | null {
    if ('name' in bead && bead.name) {
      return bead.name;
    }
    if ('beadName' in bead && bead.beadName) {
      return bead.beadName;
    }
    return null;
  }

  private getBeadColor(bead: BeadPreviewLike): string | null {
    if ('color' in bead && bead.color) {
      return bead.color;
    }
    if ('beadColor' in bead && bead.beadColor) {
      return bead.beadColor;
    }
    return null;
  }

  private getBeadDescription(bead: BeadPreviewLike): string | null {
    return bead.description || null;
  }
}
